/**
 * One scenario type, read out of its SOP's list: there is no single-type endpoint, and the
 * query keyed by SOP makes hovering ten scenarios of one type cost one request. It is the
 * back-testing page's own query, so the cache under that key holds one shape.
 */

import { useMemo } from 'react';
import { useBackTestingTypes } from '@/hooks/use-back-testing-types';
import type { ScenarioType } from '@/modules/back-testing/types';

interface ScenarioTypeState {
  type: ScenarioType | null;
  isPending: boolean;
  isError: boolean;
  /** The fetch succeeded and the type is not in it: deleted, or regenerated away. */
  missing: boolean;
}

export const useScenarioType = (sopId: string, scenarioTypeId: string): ScenarioTypeState => {
  const { data, isPending, isError } = useBackTestingTypes(sopId);

  const type = useMemo(
    () => data?.types.find((candidate) => candidate.id === scenarioTypeId) ?? null,
    [data, scenarioTypeId]
  );

  return { type, isPending, isError, missing: !isPending && !isError && Boolean(data) && type === null };
};
